/**
 * PaymentMethodPills — colourful pill-style payment method selector.
 *
 * Shape follows the product's approved design (yellow / cyan / green /
 * pink / purple pills, each with a rounded white icon badge, bold title
 * and a short subtitle). See PRD screenshots (Feb 2026).
 *
 * Method visibility rules:
 *   • Only methods with `enabled=true` (admin toggle) are shown.
 *   • PT locale: up to 5 pills — stripe, paypal, mbway, multibanco, pay_later
 *   • EN / ES locale: up to 3 pills — stripe, paypal, pay_later
 *     (MB WAY / Multibanco are Portuguese-only payment rails.)
 *   • `showPayLater` opt-out for pages where "pay later" doesn't apply
 *     (e.g. the /pagar/{token} page — user is already on the pay link).
 *   • `showBankTransfer` opt-in for the /pagar page which surfaces IBAN
 *     details separately.
 *
 * Click behaviour: this component is purely presentational — callers pass
 * `onSelect(methodKey)` and drive the checkout flow themselves.
 */
import React from "react";
import { Clock } from "lucide-react";

// Brand marks — inline SVGs matching each gateway's official identity.
// Kept small (24×24 viewBox) so they slot into the pill's white badge
// without extra styling. `currentColor` picks up the pill's icon color.

const IconCards = () => (
  // Card + Visa/MC brand pair — represents any credit/debit checkout.
  <svg viewBox="0 0 32 24" fill="none" className="w-full h-full p-1.5">
    <rect x="1" y="3" width="30" height="18" rx="2.5" fill="currentColor" opacity="0.15" />
    <rect x="1" y="3" width="30" height="18" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
    <rect x="3.5" y="7" width="7" height="3" rx="0.5" fill="currentColor" />
    <circle cx="22" cy="15" r="3.5" fill="#EB001B" />
    <circle cx="26" cy="15" r="3.5" fill="#F79E1B" opacity="0.9" />
  </svg>
);

const IconPaypal = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full p-1.5">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106-.32 2.033a.578.578 0 0 0 .567.657h4.234c.464 0 .857-.338.93-.795l.038-.198.855-5.418.055-.297a.937.937 0 0 1 .93-.79h.583c3.807 0 6.79-1.545 7.663-6.02.365-1.87.176-3.43-.788-4.617z"/>
  </svg>
);

const IconMbway = () => (
  // MB WAY — official brand uses two curved arrows (green + red) around
  // an "MB" mark. Rendered as a stylised MB inside a phone silhouette.
  <svg viewBox="0 0 32 32" fill="none" className="w-full h-full p-1">
    <rect x="9" y="3" width="14" height="26" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <rect x="14" y="26" width="4" height="1.5" rx="0.7" fill="currentColor"/>
    <text x="16" y="17" fontSize="7" fontWeight="800" textAnchor="middle" fill="#E30613" fontFamily="Arial">MB</text>
    <text x="16" y="23" fontSize="4" fontWeight="700" textAnchor="middle" fill="#00A651" fontFamily="Arial">WAY</text>
  </svg>
);

const IconMultibanco = () => (
  // Multibanco — official mark is a bold "MB" in dark blue. Kept simple
  // and readable at the badge size.
  <svg viewBox="0 0 32 32" fill="none" className="w-full h-full p-1">
    <rect x="2" y="8" width="28" height="16" rx="2" fill="#003D8F"/>
    <text x="16" y="20.5" fontSize="11" fontWeight="900" textAnchor="middle" fill="#FFFFFF" fontFamily="Arial, sans-serif">MB</text>
  </svg>
);

// Palette + brand marks — matches PRD screenshots. Colours are declared
// INLINE (via the `style` prop) rather than through Tailwind classes so
// dark-themed page wrappers (SiteChrome, admin pages) cannot cascade their
// own `color: #fff` reset over the pills and wash out the text.
const STYLE = {
  stripe:     { bg: "#fde047", fg: "#0f172a", Icon: IconCards },       // yellow-300 · slate-900
  paypal:     { bg: "#67e8f9", fg: "#0f172a", Icon: IconPaypal },      // cyan-300  · slate-900
  mbway:      { bg: "#a3e635", fg: "#0f172a", Icon: IconMbway },       // lime-400  · slate-900
  multibanco: { bg: "#ec4899", fg: "#ffffff", Icon: IconMultibanco },  // pink-500  · white
  pay_later:  { bg: "#8b5cf6", fg: "#ffffff", Icon: Clock },           // violet-500 · white
};

const T = {
  pt: {
    stripe: { title: "Cartão", subtitle: "Visa, Mastercard,\nApple Pay, Google Pay" },
    paypal: { title: "PayPal", subtitle: "Conta PayPal\nou cartão" },
    mbway: { title: "MB WAY", subtitle: "Pagamento\ninstantâneo no telemóvel" },
    multibanco: { title: "Multibanco / Payshop", subtitle: "Referência ATM ou\nhomebanking" },
    pay_later: { title: "Pagar mais tarde", subtitle: "Reservamos a encomenda;\nrecebe link no email" },
  },
  en: {
    stripe: { title: "Card", subtitle: "Visa, MC,\nApple/Google Pay" },
    paypal: { title: "PayPal", subtitle: "PayPal account\nor card" },
    pay_later: { title: "Pay later", subtitle: "Order reserved;\npay link sent by email" },
  },
  es: {
    stripe: { title: "Tarjeta", subtitle: "Visa, MC,\nApple/Google Pay" },
    paypal: { title: "PayPal", subtitle: "Cuenta PayPal o\ntarjeta" },
    pay_later: { title: "Pagar más tarde", subtitle: "Pedido reservado;\nenlace por email" },
  },
};

// (STYLE map declared above the icon components — includes brand-official marks.)

function Pill({ methodKey, title, subtitle, bg, fg, Icon, disabled, onClick, testId }) {
  const isDarkPill = fg === "#ffffff";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testId || `pill-${methodKey}`}
      // Inline styles: bg, colour AND font-weight are set here so the pill
      // renders identically inside light OR dark themed page shells (no
      // `.dark { color: #fff }` cascade can override it).
      style={{
        background: bg,
        color: fg,
        minHeight: 140,
      }}
      className={`rounded-2xl p-5 text-left shadow-sm transition-all ${
        disabled
          ? "opacity-60 cursor-not-allowed"
          : "hover:-translate-y-0.5 hover:shadow-md active:translate-y-0"
      } focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-900/30`}
    >
      <div className="flex flex-col gap-3 h-full">
        <div
          className="w-11 h-11 rounded-xl grid place-items-center overflow-hidden"
          style={{ background: "#ffffff", color: isDarkPill ? "#1f2937" : "#334155" }}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <div className="font-[Fredoka] text-lg leading-tight" style={{ color: fg, fontWeight: 700 }}>{title}</div>
          <div className="text-sm mt-1 whitespace-pre-line leading-snug" style={{ color: fg, opacity: 0.95, fontWeight: 500 }}>{subtitle}</div>
        </div>
      </div>
    </button>
  );
}

export default function PaymentMethodPills({
  lang = "pt",
  methods = {},
  onSelect,
  showPayLater = true,
  showBankTransfer = false,
  loadingKey = null,
}) {
  const l = T[lang] || T.pt;
  // Deterministic order matching the PRD screenshots.
  const order = lang === "pt"
    ? ["stripe", "paypal", "mbway", "multibanco", "pay_later"]
    : ["stripe", "paypal", "pay_later"];

  const isEnabled = (key) => {
    if (key === "pay_later") return showPayLater;
    if (key === "stripe") return !!methods.stripe?.enabled;
    if (key === "paypal") return !!methods.paypal?.enabled;
    // MB WAY + Multibanco share the same admin toggle (ifthenpay).
    if (key === "mbway" || key === "multibanco") return !!methods.ifthenpay?.enabled;
    if (key === "bank_transfer") return showBankTransfer && !!methods.bank_transfer?.enabled;
    return false;
  };

  const visible = order.filter(isEnabled);
  if (showBankTransfer && isEnabled("bank_transfer")) visible.push("bank_transfer");

  if (visible.length === 0) {
    return (
      <div className="text-sm text-slate-500 italic p-4 border border-dashed border-slate-300 rounded-xl">
        {lang === "en" ? "No payment method is currently enabled by the admin." :
         lang === "es" ? "El admin no ha activado ningún método de pago." :
         "Nenhum método de pagamento está actualmente activado pelo admin."}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="payment-pills">
      {visible.map((key) => {
        const style = STYLE[key];
        const meta = l[key] || T.pt[key];
        if (!style || !meta) return null;
        return (
          <Pill
            key={key}
            methodKey={key}
            title={meta.title}
            subtitle={meta.subtitle}
            bg={style.bg}
            fg={style.fg}
            Icon={style.Icon}
            disabled={loadingKey && loadingKey !== key}
            onClick={() => onSelect && onSelect(key)}
          />
        );
      })}
    </div>
  );
}
